import { Observable, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RETRY_COUNT, API_RETRY_DELAY } from '@shared/constants';
import { ConfigService } from '@shared/services/core/config.service';

import { ActiveReservationRepository } from './active-reservation.repository';
import { trackActiveReservationRequestsStatus } from './active-reservation.store';
import { ReservationsRepository } from './reservation.repository';
import { skipWhileReservationsCached, trackReservationsRequestsStatus } from './reservation.store';

@Injectable({ providedIn: 'root' })
export class ReservationService {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly http: HttpClient,
		private readonly repo: ReservationsRepository,
		private readonly launchRepository: ActiveReservationRepository,
		private readonly env: ConfigService
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clear
	 **-------------------------------------------------------------------------------------
	 */
	public clear() {
		this.repo.clear();
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getDailyReservations
	 * *-------------------------------------------------------------------------------------
	 */
	public getDailyReservations({ startDate, endDate }): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/daily-reservations?startDate=${startDate}${endDate ? '&endDate=' + endDate : ''}`;
		if (this.repo.queryIsCached) return this.repo.reservations$;
		return this.http.get(url, {}).pipe(
			tap(resp => this.repo.addReservations(resp)),
			trackReservationsRequestsStatus('reservations'),
			skipWhileReservationsCached('reservations'),
			shareReplay()
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - endTrip
	 **-------------------------------------------------------------------------------------
	 */
	public saveEndReservationReview(payload): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/${payload.reservation_id}/review`;
		return this.http.post(url, {
			...payload,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getReservationDetails
	 **-------------------------------------------------------------------------------------
	 */
	public getReservationDetails({ reservationId, customerId, invoiceId }): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/${reservationId}/details?customerId=${customerId}&invoiceId=${invoiceId}`;
		return this.http.get(url).pipe(
			map((resp: any) => {
				return {
					...resp.reservation[0],
					...resp,
					original: resp,
				};
			}),
			tap(resp => this.repo.addReservation(resp)),
			trackReservationsRequestsStatus(`reservation-${reservationId}`),
			skipWhileReservationsCached(`reservation-${reservationId}`)
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - cancelBooking
	 **-------------------------------------------------------------------------------------
	 */
	public cancelBooking({ reservationId, invoiceId, refund = 'yes' }): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/${reservationId}/cancel?refund=${refund}`;
		return this.http.post(url, {
			invoiceId,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getRegistrationByToken
	 **-------------------------------------------------------------------------------------
	 */
	public getRegistrationById(reservation): Observable<any> {
		const url = `${this.env.get('registrationEndPoint')}/get-reservation/${reservation.reservation_id}`;
		return this.http.post(url, { reservation }).pipe(
			retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }),
			tap((resp: any) => this.launchRepository.addLaunch(resp, reservation.state)),
			map(resp => ({ ...resp, launchType: reservation.launchType })),
			trackActiveReservationRequestsStatus(`activeReservation-${reservation.reservation_id}`)
			//skipWhileActiveReservationCached(`activeReservation-${reservation.reservation_id}`)
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateProductStatus
	 **-------------------------------------------------------------------------------------
	 */
	public createBooking({
		charges,
		customer,
		reservation,
		total,
		sendRegistrationLink,
		reservationMetaData,
		productIds,
		companyId,
		isAdmin = false,
		availableProducts,
		isMobileApp = true,
	}): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/create`;
		return this.http
			.post(url, {
				availableProducts,
				isMobileApp,
				charges,
				customer,
				reservation,
				total,
				sendRegistrationLink,
				reservationMetaData,
				productIds,
				companyId,
				isAdmin,
			})
			.pipe(
				catchError(err => {
					console.log(err);
					return throwError(err);
				})
			);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getRegistrationByToken
	 **-------------------------------------------------------------------------------------
	 */
	public registerAndPay(payload): Observable<any> {
		const url = `${this.env.get('registrationEndPoint')}/register-and-pay`;
		return this.http.post(url, { ...payload });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - assignEmployeeToReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public assignEmployeeToReservation({ reservationId, employeeId }): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/assign-employee`;
		return this.http.post(url, { reservationId, employeeId });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - assignEmployeeToReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public getPassengers(reservationId): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/${reservationId}/passenger`;
		return this.http.get(url).pipe(tap(resp => this.launchRepository.setPassengers(reservationId, resp)));
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - assignEmployeeToReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public createPassenger({
		reservation_id,
		operator_name,
		operator_dob,
		email,
		is_operator = false,
		safety_briefing_sent = false,
		is_minor = false,
		signature = null,
		signature_sign_date = null,
		signed_acknowledgement = false,
		signed_navigation_rules = false,
		signed_rental_agreement = false,
		signed_waiver = false,
		all_documents_signed = false,
		location = null,
		marina_signature = null,
	}): Observable<any> {
		const url = `${this.env.get('reservationsEndPoint')}/${reservation_id}/passenger`;
		return this.http.post(url, {
			reservation_id,
			operator_name,
			operator_dob,
			is_operator: is_operator ? true : false,
			email,
			safety_briefing_sent,
			is_minor: is_minor ? true : false,
			signature,
			signature_sign_date,
			signed_acknowledgement: signed_acknowledgement ? true : false,
			signed_navigation_rules: signed_navigation_rules ? true : false,
			signed_rental_agreement: signed_rental_agreement ? true : false,
			signed_waiver: signed_waiver ? true : false,
			all_documents_signed: all_documents_signed ? true : false,
			location,
			marina_signature,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updatePassenger
	 **-------------------------------------------------------------------------------------
	 */
	public updatePassenger({
		reservation_additional_operator_id,
		reservation_id,
		operator_name,
		operator_dob,
		email,
		is_operator = false,
		safety_briefing_sent = false,
		is_minor = false,
		signature = null,
		signature_sign_date = null,
		signed_acknowledgement = false,
		signed_navigation_rules = false,
		signed_rental_agreement = false,
		signed_waiver = false,
		all_documents_signed = false,
		location = null,
		marina_signature = null,
	}) {
		const url = `${this.env.get('reservationsEndPoint')}/${reservation_id}/passenger/${reservation_additional_operator_id}`;
		return this.http.patch(url, {
			reservation_id,
			operator_name,
			operator_dob,
			is_operator: is_operator ? true : false,
			email,
			safety_briefing_sent,
			is_minor: is_minor ? true : false,
			signature,
			signature_sign_date,
			signed_acknowledgement: signed_acknowledgement ? true : false,
			signed_navigation_rules: signed_navigation_rules ? true : false,
			signed_rental_agreement: signed_rental_agreement ? true : false,
			signed_waiver: signed_waiver ? true : false,
			all_documents_signed: all_documents_signed ? true : false,
			location,
			marina_signature,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deletePassenger
	 **-------------------------------------------------------------------------------------
	 */
	public deletePassenger(id, reservation_id) {
		const url = `${this.env.get('reservationsEndPoint')}/${reservation_id}/passenger/${id}`;
		return this.http.delete(url);
	}
}
